import { sentry } from '@frontend/tracking';
import { useSoftphoneCallState } from '..';
import { ContextualCallInfo, OutgoingCall } from '../../../types';
import { useSoftphoneAudio } from '../../softphone-audio-provider';
import { useSoftphoneClient } from '../../softphone-client-provider';
import { useSoftphoneDialer } from '../../softphone-dialer-provider';
import { useSoftphoneEventsEmitter } from '../../softphone-events-provider';
import { useSoftphoneSettings } from '../../softphone-settings-provider';
import { formatUri } from '../utils/uri-utils';
import { useToggleHold } from './use-hold-call';

export const usePlaceCall = () => {
  const client = useSoftphoneClient((ctx) => ctx.client);
  const domain = useSoftphoneClient((ctx) => ctx.domain);
  const resolvedDialAddress = useSoftphoneDialer((ctx) => ctx.resolvedDialAddress);
  const setIsPlaceCallLoading = useSoftphoneCallState((ctx) => ctx.setIsPlaceCallLoading);
  const { emit } = useSoftphoneEventsEmitter();
  const currentPhoneNumber = useSoftphoneSettings((ctx) => ctx.selectedOutboundPhoneNumber?.number);
  const restart = useSoftphoneClient((ctx) => ctx.restart);
  const { handleOutgoingCall } = useHandleOutgoingCall();
  const stream = useSoftphoneAudio((ctx) => ctx.stream);

  const placeCallDirect = (numOrURI: string, callInfo?: ContextualCallInfo) => {
    if (!client) {
      return Promise.reject(new Error('No Client'));
    }

    if (!numOrURI) {
      return Promise.reject(new Error('No Phone number or URI'));
    }
    setIsPlaceCallLoading(true);

    const formattedUri = formatUri(numOrURI, domain);

    return client
      .call(
        `sip:${formattedUri}`,
        {
          extraHeaders: [`P-Asserted-Identity: <sip:${currentPhoneNumber}@${domain}>`],
          earlyMedia: true,
          delegate: {
            onSessionDescriptionHandler(sessionDescriptionHandler) {
              // Set up early media handling here
              // @ts-ignore - this works but is not documented in sip.js types
              sessionDescriptionHandler.peerConnectionDelegate = {
                ontrack: (event: RTCTrackEvent) => {
                  const track = event.track;
                  if (track && track.kind === 'audio') {
                    const remoteStream = new MediaStream();
                    remoteStream.addTrack(track);
                    stream('local', remoteStream);
                  }
                },
              };
            },
          },
        },
        {
          requestDelegate: {
            onReject(response) {
              console.log('onReject invite', response);
              if (response.message.statusCode === 403) {
                restart();
              }
            },
          },
        }
      )
      .then((inviter) => {
        const call = {
          id: inviter.id,
          inviter: inviter,
          placedAt: new Date(),
          onHoldSince: undefined,
          remoteParty: {
            displayName: inviter.remoteIdentity.displayName || undefined,
            uri: inviter.remoteIdentity.uri.user ?? 'unknown',
          },
          ...callInfo,
        } satisfies OutgoingCall;

        handleOutgoingCall(call);

        return call;
      })
      .catch((err) => {
        emit('outgoing-call.failed', err);
        sentry.error({
          error: err,
          topic: 'softphone',
          team: 'fe-phone-config',
          addContext: {
            name: 'placeCallDirect',
            context: {
              numOrURI,
            },
          },
        });
        throw err;
      })
      .finally(() => setIsPlaceCallLoading(false));
  };

  const placeCallFromDialer = (callInfo?: ContextualCallInfo) => {
    if (!resolvedDialAddress) {
      return Promise.reject(new Error('No Phone number or URI'));
    }

    return placeCallDirect(resolvedDialAddress, callInfo);
  };

  return {
    placeCallDirect,
    placeCallFromDialer,
  };
};

export const useHandleOutgoingCall = () => {
  const addCall = useSoftphoneCallState((ctx) => ctx.addCall);
  const setPrimaryCall = useSoftphoneCallState((ctx) => ctx.setPrimaryCall);
  const { toggleHoldAll } = useToggleHold();
  const { emit } = useSoftphoneEventsEmitter();

  const handleOutgoingCall = (call: OutgoingCall) => {
    toggleHoldAll(true);
    addCall(call);
    setPrimaryCall(call.id);
    emit('outgoing-call.sent', call);
    return call;
  };

  return { handleOutgoingCall };
};
